import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Plus, Trash2, Save, Calendar, Clock, DollarSign, Loader, ChevronDown, ChevronUp } from 'lucide-react'
import { db, COLLECTIONS } from '../firebase/config'
import PageHelpButton from '../components/PageHelpButton'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import toast from 'react-hot-toast'

const STORAGE_KEY = 'scheduleSettings'

// Ghana regions (16)
const GHANA_REGIONS = [
  'Ahafo', 'Ashanti', 'Bono', 'Bono East', 'Central', 'Eastern',
  'Greater Accra', 'North East', 'Northern', 'Oti', 'Savannah',
  'Upper East', 'Upper West', 'Volta', 'Western', 'Western North'
]

const defaultSettings = {
  postBookingIntervalMinutes: 120, // buffer after last booking + service duration
  rules: [], // list of unavailability rules
  allowedRegions: GHANA_REGIONS, // default: all regions allowed
  allowedVisitTypes: ['spa', 'mobile'], // default: both visit types enabled
  applyPaystackFeeUplift: true, // whether client site adds 2% to cover Paystack fees
}

const defaultDepositSettings = {
  mode: 'percentage', // 'percentage' | 'fixed'
  value: 10 // 10% by default
}

// Helper to generate a readable label for rules
function formatRuleLabel(rule) {
  if (rule.type === 'one_off') {
    return `${rule.date} ${rule.startTime}–${rule.endTime}`
  }
  if (rule.type === 'weekly') {
    return `Every ${rule.weekday} ${rule.startTime}–${rule.endTime}`
  }
  if (rule.type === 'monthly') {
    return `Every month on day ${rule.dayOfMonth} ${rule.startTime}–${rule.endTime}`
  }
  if (rule.type === 'daily') {
    return `Every day ${rule.startTime}–${rule.endTime}`
  }
  return 'Rule'
}

const Settings = () => {
  const [settings, setSettings] = useState(defaultSettings)
  const [depositSettings, setDepositSettings] = useState(defaultDepositSettings)
  const [regionsOpen, setRegionsOpen] = useState(false)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [loading, setLoading] = useState(true)
  const [depositSectionOpen, setDepositSectionOpen] = useState(false)
  const [intervalSectionOpen, setIntervalSectionOpen] = useState(false)
  const [regionsSectionOpen, setRegionsSectionOpen] = useState(false)
  const [visitTypesSectionOpen, setVisitTypesSectionOpen] = useState(false)
  const [rulesSectionOpen, setRulesSectionOpen] = useState(false)

  const [newRule, setNewRule] = useState({
    type: 'one_off', // 'one_off' | 'weekly' | 'monthly' | 'daily'
    date: '', // yyyy-mm-dd for one_off
    weekday: 'Monday', // for weekly
    dayOfMonth: 1, // for monthly
    startTime: '', // HH:MM
    endTime: '', // HH:MM
    note: ''
  })

  // Load from Firestore on mount
  useEffect(() => {
    try {
      (async () => {
        setLoading(true)
        const settingsDocRef = doc(db, COLLECTIONS.SETTINGS, 'booking')
        const snap = await getDoc(settingsDocRef)
        
        if (snap.exists()) {
          const data = snap.data()
          const loadedRules = Array.isArray(data.rules) ? data.rules : []
          setSettings({
            postBookingIntervalMinutes: data.postBookingIntervalMinutes ?? defaultSettings.postBookingIntervalMinutes,
            rules: loadedRules,
            allowedRegions: Array.isArray(data.allowedRegions) && data.allowedRegions.length > 0 
              ? data.allowedRegions 
              : defaultSettings.allowedRegions,
            allowedVisitTypes: Array.isArray(data.allowedVisitTypes) && data.allowedVisitTypes.length > 0
              ? data.allowedVisitTypes
              : defaultSettings.allowedVisitTypes,
            applyPaystackFeeUplift: typeof data.applyPaystackFeeUplift === 'boolean'
              ? data.applyPaystackFeeUplift
              : defaultSettings.applyPaystackFeeUplift,
          })
          
          // Load deposit settings from Firestore
          if (data.depositSettings) {
            setDepositSettings({
              mode: data.depositSettings.mode === 'fixed' ? 'fixed' : 'percentage',
              value: Number(data.depositSettings.value) || defaultDepositSettings.value
            })
          } else {
            // Migrate from localStorage if Firestore doesn't have it
            try {
              const raw = localStorage.getItem('depositSettings')
              if (raw) {
                const parsed = JSON.parse(raw)
                if (parsed && typeof parsed === 'object') {
                  const migratedSettings = {
                    mode: parsed.mode === 'fixed' ? 'fixed' : 'percentage',
                    value: Number(parsed.value) || defaultDepositSettings.value
                  }
                  setDepositSettings(migratedSettings)
                  // Save to Firestore for future use
                  await setDoc(settingsDocRef, {
                    ...data,
                    depositSettings: migratedSettings,
                    updatedAt: new Date().toISOString()
                  }, { merge: true })
                }
              }
            } catch {}
          }
        } else {
          // No Firestore data, use defaults
          setSettings(defaultSettings)
          // Try to migrate from localStorage
          try {
            const raw = localStorage.getItem('depositSettings')
            if (raw) {
              const parsed = JSON.parse(raw)
              if (parsed && typeof parsed === 'object') {
                const migratedSettings = {
                  mode: parsed.mode === 'fixed' ? 'fixed' : 'percentage',
                  value: Number(parsed.value) || defaultDepositSettings.value
                }
                setDepositSettings(migratedSettings)
              }
            }
          } catch {}
        }
        
        // Mark initial load as complete
        setIsInitialLoad(false)
        setLoading(false)
      })()
    } catch (error) {
      console.error('Failed to load settings from Firestore:', error)
      toast.error('Failed to load settings from Firestore')
      // Fallback to defaults on error
      setSettings(defaultSettings)
      setIsInitialLoad(false)
      setLoading(false)
    }
  }, [])

  // Save deposit settings to Firestore whenever they change (after initial load)
  useEffect(() => {
    if (isInitialLoad) return // Don't save on initial load
    
    const saveDepositSettings = async () => {
      try {
        const settingsDocRef = doc(db, COLLECTIONS.SETTINGS, 'booking')
        await setDoc(settingsDocRef, {
          depositSettings: {
            mode: depositSettings.mode,
            value: depositSettings.value
          },
          updatedAt: new Date().toISOString()
        }, { merge: true })
      } catch (error) {
        console.error('Failed to save deposit settings to Firestore:', error)
        toast.error('Failed to save deposit settings')
      }
    }
    
    saveDepositSettings()
  }, [depositSettings, isInitialLoad])

  // Helper function to save settings to Firestore
  const saveSettingsToFirestore = async () => {
    try {
      const settingsDocRef = doc(db, COLLECTIONS.SETTINGS, 'booking')
      await setDoc(settingsDocRef, { 
        postBookingIntervalMinutes: settings.postBookingIntervalMinutes,
        rules: settings.rules,
        allowedRegions: settings.allowedRegions || [],
        allowedVisitTypes: settings.allowedVisitTypes || defaultSettings.allowedVisitTypes,
        applyPaystackFeeUplift: settings.applyPaystackFeeUplift,
        depositSettings: {
          mode: depositSettings.mode,
          value: depositSettings.value
        },
        updatedAt: new Date().toISOString()
      }, { merge: true })
      return true
    } catch (e) {
      console.error('Failed to save settings to Firestore:', e)
      toast.error('Failed to save settings to Firestore')
      return false
    }
  }

  // Persist all booking settings to Firestore when they change (but not on initial load)
  useEffect(() => {
    // Skip saving on initial load
    if (isInitialLoad) {
      return
    }
    
    saveSettingsToFirestore()
  }, [
    settings.postBookingIntervalMinutes,
    settings.rules,
    settings.allowedRegions,
    settings.allowedVisitTypes,
    settings.applyPaystackFeeUplift,
    isInitialLoad
  ])

  const addRule = async () => {
    // Basic validation
    if (!newRule.startTime || !newRule.endTime) {
      toast.error('Please provide both start and end times')
      return
    }

    if (newRule.type === 'one_off' && !newRule.date) {
      toast.error('Please select a date for one-off rule')
      return
    }
    if (newRule.type === 'monthly' && (!newRule.dayOfMonth || newRule.dayOfMonth < 1 || newRule.dayOfMonth > 31)) {
      toast.error('Please enter a valid day of month (1-31)')
      return
    }

    const ruleToAdd = {
      id: Date.now(),
      type: newRule.type,
      startTime: newRule.startTime,
      endTime: newRule.endTime,
      note: newRule.note || '',
      ...(newRule.type === 'monthly' && { dayOfMonth: Number(newRule.dayOfMonth) }),
      ...(newRule.type === 'weekly' && { weekday: newRule.weekday }),
      ...(newRule.type === 'one_off' && { date: newRule.date })
    }
    
    // Calculate updated rules array
    const updatedRules = [...settings.rules, ruleToAdd]
    
    // Save to Firestore first
    try {
      const settingsDocRef = doc(db, COLLECTIONS.SETTINGS, 'booking')
      const dataToSave = { 
        postBookingIntervalMinutes: settings.postBookingIntervalMinutes,
        rules: updatedRules,
        allowedRegions: settings.allowedRegions || [],
        applyPaystackFeeUplift: settings.applyPaystackFeeUplift,
        updatedAt: new Date().toISOString()
      }
      
      await setDoc(settingsDocRef, dataToSave, { merge: true })
      
      // Only update local state after successful save
      setSettings(prev => ({ ...prev, rules: updatedRules }))
      toast.success('Unavailability rule added and saved to Firestore')
      
      // Reset form (keep type for faster input)
      setNewRule(prev => ({
        type: prev.type,
        date: '',
        weekday: 'Monday',
        dayOfMonth: 1,
        startTime: '',
        endTime: '',
        note: ''
      }))
    } catch (error) {
      console.error('Failed to save rule to Firestore:', error)
      toast.error(`Failed to save rule to Firestore: ${error.message}`)
    }
  }

  const deleteRule = async (id) => {
    // Store original rule for potential revert
    const originalRule = settings.rules.find(r => r.id === id)
    if (!originalRule) {
      toast.error('Rule not found')
      return
    }
    
    // Calculate updated rules array
    const updatedRules = settings.rules.filter(r => r.id !== id)
    
    // Save to Firestore first
    try {
      const settingsDocRef = doc(db, COLLECTIONS.SETTINGS, 'booking')
      const dataToSave = { 
        postBookingIntervalMinutes: settings.postBookingIntervalMinutes,
        rules: updatedRules,
        allowedRegions: settings.allowedRegions || [],
        applyPaystackFeeUplift: settings.applyPaystackFeeUplift,
        updatedAt: new Date().toISOString()
      }
      
      await setDoc(settingsDocRef, dataToSave, { merge: true })
      
      // Only update local state after successful save
      setSettings(prev => ({ ...prev, rules: updatedRules }))
      toast.success('Unavailability rule deleted and saved to Firestore')
    } catch (error) {
      console.error('Failed to delete rule from Firestore:', error)
      toast.error(`Failed to delete rule from Firestore: ${error.message}`)
    }
  }

  const displayedIntervalLabel = useMemo(() => {
    const mins = Number(settings.postBookingIntervalMinutes) || 0
    if (mins % 60 === 0) return `${mins / 60} hour${mins === 60 ? '' : 's'}`
    return `${mins} minutes`
  }, [settings.postBookingIntervalMinutes])

  return (
    <div className="space-y-6 text-white">
      <div>
        <h1 className="text-2xl font-semibold text-white">Settings</h1>
        <p className="text-gray-300 mt-1">Configure booking schedules, availability, and deposit settings</p>
      </div>

      <PageHelpButton
        videoId={9}
        title="Settings page tutorial"
        youtubeUrl="https://www.youtube.com/embed/zEhGFOSxr4M"
      />

      {/* Deposit Settings - dropdown */}
      <div className="bg-gray-800 bg-opacity-50 backdrop-blur-sm rounded-lg border border-gray-700 overflow-hidden">
        <button
          type="button"
          onClick={() => {
            setDepositSectionOpen(prev => {
              const next = !prev
              if (next) {
                setIntervalSectionOpen(false)
                setRegionsSectionOpen(false)
                setVisitTypesSectionOpen(false)
                setRulesSectionOpen(false)
              }
              return next
            })
          }}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-700/50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <DollarSign className="w-5 h-5 text-emerald-400" />
            <h2 className="text-lg font-semibold text-white">Initial Deposit</h2>
          </div>
          {depositSectionOpen ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
        </button>
        {depositSectionOpen && (
        <div className="border-t border-gray-700 p-6">
        <p className="text-gray-300 mb-4">Configure initial booking deposit shown on the main site</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Mode</label>
            <select
              className="input-field bg-gray-700 border-gray-600 text-white"
              value={depositSettings.mode}
              onChange={(e)=>setDepositSettings(prev=>({ ...prev, mode: e.target.value }))}
            >
              <option value="percentage">Percentage of price</option>
              <option value="fixed">Fixed amount</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">{depositSettings.mode === 'percentage' ? 'Percentage (%)' : 'Fixed Amount (GHS)'} </label>
            <input
              type="number"
              min="0"
              step={depositSettings.mode === 'percentage' ? '1' : '0.01'}
              className="input-field bg-gray-700 border-gray-600 text-white"
              value={depositSettings.value}
              onChange={(e)=>setDepositSettings(prev=>({ ...prev, value: Number(e.target.value) }))}
            />
            <p className="text-xs text-gray-400 mt-2">
              {depositSettings.mode === 'percentage' ? 'Deposit will be this percentage of the service price.' : 'Deposit will be this exact amount regardless of service price.'}
            </p>
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-gray-700">
          <h3 className="text-md font-semibold text-white mb-2">Paystack Charges Fee Uplift</h3>
          <p className="text-gray-300 text-sm mb-3">
            When enabled, card payments on the main site add a 2% increase to cover Paystack charges.
            When disabled, the exact service price is sent to Paystack.
          </p>
          <label className="inline-flex items-center space-x-3">
            <button
              type="button"
              onClick={() => setSettings(prev => ({ 
                ...prev, 
                applyPaystackFeeUplift: !prev.applyPaystackFeeUplift 
              }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.applyPaystackFeeUplift ? 'bg-emerald-500' : 'bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.applyPaystackFeeUplift ? 'translate-x-5' : 'translate-x-1'
                }`}
              />
            </button>
            <span className="text-sm text-gray-200">
              {settings.applyPaystackFeeUplift ? '2% uplift enabled' : 'Send exact prices (no uplift)'}
            </span>
          </label>
        </div>
        </div>
        )}
      </div>

      {/* Post-booking interval - dropdown */}
      <div className="bg-gray-800 bg-opacity-50 backdrop-blur-sm rounded-lg border border-gray-700 overflow-hidden">
        <button
          type="button"
          onClick={() => {
            setIntervalSectionOpen(prev => {
              const next = !prev
              if (next) {
                setDepositSectionOpen(false)
                setRegionsSectionOpen(false)
                setRulesSectionOpen(false)
                setVisitTypesSectionOpen(false)
              }
              return next
            })
          }}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-700/50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <Clock className="w-5 h-5 text-green-400" />
            <h2 className="text-lg font-semibold text-white">Post-booking Interval</h2>
          </div>
          {intervalSectionOpen ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
        </button>
        {intervalSectionOpen && (
        <div className="border-t border-gray-700 p-6">
        <p className="text-gray-300 mb-4">Time added after a booking finishes before the next booking can start.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Interval (minutes)</label>
            <input
              type="number"
              min="0"
              step="15"
              className="input-field bg-gray-700 border-gray-600 text-white"
              value={settings.postBookingIntervalMinutes}
              onChange={(e) => setSettings(prev => ({ ...prev, postBookingIntervalMinutes: Number(e.target.value) }))}
              placeholder="120"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Readable</label>
            <input
              type="text"
              readOnly
              className="input-field bg-gray-700 border-gray-700 text-gray-300"
              value={displayedIntervalLabel}
            />
          </div>
        </div>
        </div>
        )}
      </div>

      {/* Visit Types - dropdown */}
      <div className="bg-gray-800 bg-opacity-50 backdrop-blur-sm rounded-lg border border-gray-700 overflow-hidden">
        <button
          type="button"
          onClick={() => {
            setVisitTypesSectionOpen(prev => {
              const next = !prev
              if (next) {
                setDepositSectionOpen(false)
                setIntervalSectionOpen(false)
                setRegionsSectionOpen(false)
                setRulesSectionOpen(false)
              }
              return next
            })
          }}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-700/50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <Calendar className="w-5 h-5 text-emerald-300" />
            <h2 className="text-lg font-semibold text-white">Visit Types Available</h2>
          </div>
          {visitTypesSectionOpen ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>
        {visitTypesSectionOpen && (
        <div className="border-t border-gray-700 p-6">
          <p className="text-gray-300 mb-4">
            Select which visit types are currently available. The main site booking and gift card forms
            will only show the selected options, and service prices will be shown for the active visit type(s).
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
            <label className="flex items-center gap-3 px-3 py-2 rounded-lg bg-gray-900 border border-gray-700">
              <input
                type="checkbox"
                className="rounded"
                checked={settings.allowedVisitTypes?.includes('spa')}
                onChange={(e) =>
                  setSettings(prev => {
                    const set = new Set(prev.allowedVisitTypes || [])
                    if (e.target.checked) set.add('spa')
                    else set.delete('spa')
                    if (set.size === 0) set.add('spa')
                    return { ...prev, allowedVisitTypes: Array.from(set) }
                  })
                }
              />
              <div>
                <p className="text-sm text-white font-medium">Spa (come to our spa)</p>
                <p className="text-xs text-gray-400">
                  Enable this when you&apos;re ready to welcome guests at a spa location.
                </p>
              </div>
            </label>
            <label className="flex items-center gap-3 px-3 py-2 rounded-lg bg-gray-900 border border-gray-700">
              <input
                type="checkbox"
                className="rounded"
                checked={settings.allowedVisitTypes?.includes('mobile')}
                onChange={(e) =>
                  setSettings(prev => {
                    const set = new Set(prev.allowedVisitTypes || [])
                    if (e.target.checked) set.add('mobile')
                    else set.delete('mobile')
                    if (set.size === 0) set.add('mobile')
                    return { ...prev, allowedVisitTypes: Array.from(set) }
                  })
                }
              />
              <div>
                <p className="text-sm text-white font-medium">Mobile Spa (we come to them)</p>
                <p className="text-xs text-gray-400">
                  Home/mobile spa service. Perfect while you don&apos;t yet have a physical space.
                </p>
              </div>
            </label>
          </div>
        </div>
        )}
      </div>

      {/* Mobile Spa Regions - dropdown */}
      <div className="bg-gray-800 bg-opacity-50 backdrop-blur-sm rounded-lg border border-gray-700 overflow-hidden">
        <button
          type="button"
          onClick={() => {
            setRegionsSectionOpen(prev => {
              const next = !prev
              if (next) {
                setDepositSectionOpen(false)
                setIntervalSectionOpen(false)
                setRulesSectionOpen(false)
                setVisitTypesSectionOpen(false)
              }
              return next
            })
          }}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-700/50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <Calendar className="w-5 h-5 text-amber-400" />
            <h2 className="text-lg font-semibold text-white">Mobile Spa Regions</h2>
          </div>
          {regionsSectionOpen ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
        </button>
        {regionsSectionOpen && (
        <div className="border-t border-gray-700 p-6">
        <p className="text-gray-300 mb-4">Choose regions where mobile spa is available.</p>
        <div className="relative max-w-lg z-20">
          {/* Trigger */}
          <button
            type="button"
            onClick={() => setRegionsOpen(o => !o)}
            className="w-full flex items-center justify-between bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-md px-4 py-3 text-gray-100"
          >
            <span className="text-sm">
              {(settings.allowedRegions?.length || 0) > 0
                ? `${settings.allowedRegions.length} region${settings.allowedRegions.length === 1 ? '' : 's'} selected`
                : 'Select regions'}
            </span>
            <svg className={`w-4 h-4 transition-transform ${regionsOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.19l3.71-3.96a.75.75 0 111.08 1.04l-4.25 4.53a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd"/></svg>
          </button>

          {/* Regions sheet - portaled to body so Layout overflow doesn't clip; scrollable list on all screens */}
          {regionsOpen && typeof document !== 'undefined' && createPortal(
            <div className="fixed inset-0 z-[10000]" style={{ isolation: 'isolate' }}>
              <button
                type="button"
                aria-label="Close regions"
                className="absolute inset-0 bg-black/50"
                onClick={() => setRegionsOpen(false)}
              />
              <div className="absolute inset-x-0 bottom-0 top-[10vh] max-w-xl mx-auto bg-gray-900 border-t border-gray-700 rounded-t-2xl shadow-2xl flex flex-col">
                <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-gray-700">
                  <h3 className="text-base font-semibold text-gray-100">Select Regions</h3>
                  <div className="flex items-center gap-2">
                  <button
                    type="button"
                      className="px-3 py-1.5 text-sm font-medium text-gray-300 hover:text-white"
                    onClick={() => setRegionsOpen(false)}
                    >
                      Done
                    </button>
                    <button
                      type="button"
                      aria-label="Close"
                      className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700"
                      onClick={() => setRegionsOpen(false)}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4" style={{ WebkitOverflowScrolling: 'touch' }}>
                  <div className="grid grid-cols-1 gap-2 pb-4">
                    {GHANA_REGIONS.map((region) => {
                      const checked = settings.allowedRegions?.includes(region)
                      return (
                        <label
                          key={region}
                          className="flex items-center gap-3 px-3 py-3 rounded-lg bg-gray-800 border border-gray-700 active:bg-gray-700"
                        >
                          <input
                            type="checkbox"
                            className="rounded"
                            checked={!!checked}
                            onChange={(e) =>
                              setSettings(prev => {
                                const set = new Set(prev.allowedRegions || [])
                                if (e.target.checked) set.add(region)
                                else set.delete(region)
                                return { ...prev, allowedRegions: Array.from(set) }
                              })
                            }
                          />
                          <span className="text-sm text-gray-200">{region}</span>
                        </label>
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )}
        </div>
        </div>
        )}
      </div>

      {/* Unavailability Rules - dropdown */}
      <div className="bg-gray-800 bg-opacity-50 backdrop-blur-sm rounded-lg border border-gray-700 overflow-hidden">
        <button
          type="button"
          onClick={() => {
            setRulesSectionOpen(prev => {
              const next = !prev
              if (next) {
                setDepositSectionOpen(false)
                setIntervalSectionOpen(false)
                setRegionsSectionOpen(false)
              }
              return next
            })
          }}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-700/50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <Calendar className="w-5 h-5 text-blue-400" />
            <h2 className="text-lg font-semibold text-white">Unavailability Rules</h2>
          </div>
          {rulesSectionOpen ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
        </button>
        {rulesSectionOpen && (
        <div className="border-t border-gray-700 p-6">
        <p className="text-gray-300 mb-4">All times are bookable by default. Add rules for days or times you will not be available.</p>
        {/* Add Rule Form */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Rule Type</label>
            <select
              className="input-field bg-gray-700 border-gray-600 text-white"
              value={newRule.type}
              onChange={(e) => setNewRule(prev => ({ ...prev, type: e.target.value }))}
            >
              <option value="one_off">One-off date</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly (day of month)</option>
              <option value="daily">Daily</option>
            </select>
          </div>

          {newRule.type === 'one_off' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Date</label>
              <input
                type="date"
                className="input-field bg-gray-700 border-gray-600 text-white"
                value={newRule.date}
                onChange={(e) => setNewRule(prev => ({ ...prev, date: e.target.value }))}
              />
            </div>
          )}

          {newRule.type === 'weekly' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Weekday</label>
              <select
                className="input-field bg-gray-700 border-gray-600 text-white"
                value={newRule.weekday}
                onChange={(e) => setNewRule(prev => ({ ...prev, weekday: e.target.value }))}
              >
                {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
          )}

          {newRule.type === 'monthly' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Day of Month</label>
              <input
                type="number"
                min="1"
                max="31"
                className="input-field bg-gray-700 border-gray-600 text-white"
                value={newRule.dayOfMonth}
                onChange={(e) => setNewRule(prev => ({ ...prev, dayOfMonth: e.target.value }))}
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Start Time</label>
            <input
              type="time"
              className="input-field bg-gray-700 border-gray-600 text-white"
              value={newRule.startTime}
              onChange={(e) => setNewRule(prev => ({ ...prev, startTime: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">End Time</label>
            <input
              type="time"
              className="input-field bg-gray-700 border-gray-600 text-white"
              value={newRule.endTime}
              onChange={(e) => setNewRule(prev => ({ ...prev, endTime: e.target.value }))}
            />
          </div>
          <div className="md:col-span-3">
            <label className="block text-sm font-medium text-gray-300 mb-2">Note (optional)</label>
            <input
              type="text"
              className="input-field bg-gray-700 border-gray-600 text-white"
              placeholder="e.g., Out of town, personal appointment"
              value={newRule.note}
              onChange={(e) => setNewRule(prev => ({ ...prev, note: e.target.value }))}
            />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button onClick={addRule} className="btn-primary flex items-center space-x-2">
            <Plus className="w-4 h-4" />
            <span>Add Rule</span>
          </button>
        </div>

        {/* Rules Table */}
        <div className="mt-6 overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Rule</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Note</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-600">
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center">
                    <div className="flex items-center justify-center space-x-2 text-gray-300">
                      <Loader className="w-5 h-5 animate-spin" />
                      <span>Loading rules...</span>
                    </div>
                  </td>
                </tr>
              ) : settings.rules.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-4 text-gray-400">No rules added. All times are currently bookable.</td>
                </tr>
              ) : (
                settings.rules.map((rule) => (
                  <tr key={rule.id} className="hover:bg-gray-700 bg-opacity-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{formatRuleLabel(rule)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{rule.type.replace('_',' ')}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{rule.note || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => deleteRule(rule.id)}
                        className="text-red-400 hover:text-red-300 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        </div>
        )}
      </div>
    </div>
  )
}

export default Settings
